import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import cloneDeep from "lodash/cloneDeep";
import pick from "lodash/pick";
import omit from "lodash/omit";
import uniqueId from "lodash/uniqueId";
import {
    DragDropContext,
    DropResult,
    DragStart,
    DragUpdate,
    ResponderProvided,
    DraggableLocation,
} from "react-beautiful-dnd";
import {
    Accessibility,
    Box,
    Button,
    Dialog,
    Flex,
    FocusZoneTabbableElements,
    MenuItem,
    ProviderConsumer as FluentUIThemeConsumer,
    SiteVariablesPrepared,
    getCode,
    keyboardKey,
    AccessibilityDefinition,
} from "@fluentui/react-northstar";
import { AddIcon, EditIcon, TrashCanIcon } from "@fluentui/react-icons-northstar";
import { BoardTheme } from "./BoardTheme";
import { TUsers } from "../../types/types";
import { getText, interpolate, TTranslations } from "../../translations";
import { BoardLane, TPlaceholderPosition, TBoardLanes, TBoardLane } from "./BoardLane";
import { TBoardItems, IBoardItem, IPreparedBoardItems, IPreparedBoardItem, IBoardItemCardLayout } from "./BoardItem";
import { BoardItemDialog, BoardItemDialogAction } from "./BoardItemDialog";
import { prepareBoardItems } from "./boardUtils";

const boardBehavior: () => AccessibilityDefinition = () => ({
    attributes: {
        root: {
            role: "region",
            "aria-label": "Board lanes",
        },
    },
    focusZone: {
        props: {
            shouldEnterInnerZone: (event: any) => getCode(event) === keyboardKey.Enter,
            handleTabKey: FocusZoneTabbableElements.all,
        },
    },
});

const defaultBoardItemCardLayout: IBoardItemCardLayout = {
    previewPosition: "top",
    overflowPosition: "footer",
};

/**
 * This payload is emitted when the user updates the Board’s lanes, which occurs when the user adds
 * or removes a lane, or rearranges the lanes.
 * @public
 */
export interface IBoardInteractionUpdateLanes {
    event: "update";
    target: "lanes";
    lanes: TBoardLanes;
}

/**
 * This payload is emitted when the user updates the Board’s items, which occurs when the user
 * creates a new item, deletes an item, or edits an item.
 * @public
 */
export interface IBoardInteractionUpdateItems {
    event: "update";
    target: "items";
    items: IPreparedBoardItems;
}

/**
 * The interaction payloads emitted by the Board component is either an update of the Board’s lanes,
 * or an update of the Board’s items.
 * @public
 */
export type TBoardInteraction = IBoardInteractionUpdateLanes | IBoardInteractionUpdateItems;

/**
 * The Board component can be used to render kanban and task board experiences in your app. Designs
 * for this component are available in the [Task board page of the Microsoft Teams UI Kit](https://www.figma.com/file/EOsbapNvZgEwcA1mShswfh/Microsoft-Teams-UI-Kit-Community?node-id=3789%3A3840).
 * @public
 */
export interface IBoardProps {
    /**
     * The users the Board’s items may associate with. To improve performance, this object should contain
     * only the users associated with any items in the Board.
     */
    users: TUsers;
    /**
     * The Board’s lanes, or columns.
     */
    lanes: TBoardLanes;
    /**
     * The Board’s items.
     */
    items: TBoardItems;
    /**
     * If the cards representing the Board’s items should be different from the default, that design
     * can be configured here.
     */
    boardItemCardLayout?: IBoardItemCardLayout;
    /**
     * The empty state Communication component to render if this Component has no content.
     */
    //emptyState?: TCommunicationProps;
    /**
     * The Board’s interaction handler, called when the user changes the Board’s items or lanes.
     */
    onInteraction?: (interaction: TBoardInteraction) => void;
    onShowTaskDetails: (taskId: number) => void;
    onChangeTaskStatus: (taskId: number, previousTaskId: number, status: string) => Promise<boolean>;
    chosenTaskId: number;
}

interface IBoardStandaloneProps {
    users: TUsers;
    arrangedLanes: TBoardLanes;
    setArrangedLanes: Dispatch<SetStateAction<TBoardLanes>>;
    arrangedItems: IPreparedBoardItems;
    setArrangedItems: Dispatch<SetStateAction<IPreparedBoardItems>>;
    boardItemCardLayout?: IBoardItemCardLayout;
    addingLane: boolean;
    setAddingLane: Dispatch<SetStateAction<boolean>>;
    t: TTranslations;
    rtl: boolean;
    onClick: (taskId: number) => void;
    chosenTaskId: number;
    onUpdateItemPosition: (taskId: number, previousTaskId: number, status: string) => Promise<boolean>;
}

const resetOrder = (item: IPreparedBoardItem, newOrder: number) => {
    item.order = newOrder;
    return item;
};

const getDraggable = (draggableId: string) =>
    document.querySelector(`[data-rbd-drag-handle-draggable-id='${draggableId}']`);
const getDroppable = (droppableId: string) => document.querySelector(`[data-rbd-droppable-id='${droppableId}']`);

const getClientYChildren = ($parent: Element, draggableId: string, endIndex: number) =>
    Array.from($parent.children)
        .filter(($child) => {
            const childDraggableId = $child.getAttribute("data-rbd-draggable-id");
            return typeof childDraggableId === "string" && childDraggableId !== draggableId;
        })
        .slice(0, endIndex);

const getPlaceholderPosition = ($draggable: Element, clientYChildren: Element[]): TPlaceholderPosition => {
    if (!$draggable || !$draggable.parentNode) return null;

    const { clientHeight, clientWidth } = $draggable;

    const clientY = clientYChildren.reduce((acc, $child) => {
        return acc + $child.clientHeight + 8;
    }, 2);

    const clientX = 20;

    return [clientX, clientY, clientWidth, clientHeight];
};

const BoardStandalone = (props: IBoardStandaloneProps) => {
    const {
        users,
        arrangedLanes,
        setArrangedLanes,
        arrangedItems,
        setArrangedItems,
        addingLane,
        setAddingLane,
        t,
        rtl,
    } = props;

    const [placeholderPosition, setPlaceholderPosition] = useState<TPlaceholderPosition>(null);

    const updateItemPosition = async (
        boardItem: IPreparedBoardItem,
        source: DraggableLocation,
        destination: DraggableLocation
    ) => {
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        const newStatus = destination.droppableId;

        let previousTaskId = 0;

        if (destination.index > 0) {
            const itemsForCurrentLane = arrangedItems[newStatus];
            previousTaskId = itemsForCurrentLane[destination.index - 1].id;
        }

        return await props.onUpdateItemPosition(boardItem.id, previousTaskId, newStatus);
    };

    const onDragStart = (event: DragStart, provided: ResponderProvided) => {
        const laneKey = event.source.droppableId;
        const itemKey = event.draggableId;
        const boardLane = arrangedLanes[laneKey];
        const boardItem = arrangedItems[laneKey].find((boardItem) => boardItem.itemKey === itemKey);

        const announcement = interpolate(t["on drag start board item"], [
            {
                itemTitle: getText(t.locale, boardItem!.title),
                itemPosition: arrangedItems[laneKey].indexOf(boardItem!) + 1,
                laneLength: arrangedItems[laneKey].length,
                laneTitle: getText(t.locale, boardLane.title),
            },
        ]);

        provided.announce(announcement);

        const $draggable = getDraggable(event.draggableId);
        if (!$draggable || !$draggable.parentNode) return;
        setPlaceholderPosition(
            getPlaceholderPosition(
                $draggable,
                getClientYChildren($draggable.parentNode as Element, event.draggableId, event.source.index)
            )
        );
    };

    const onDragUpdate = (event: DragUpdate, provided: ResponderProvided) => {
        if (!event.destination) return;
        const $draggable = getDraggable(event.draggableId);
        const $droppable = getDroppable(event.destination.droppableId);
        if (!$draggable || !$droppable) return;

        const destinationLaneKey = event.destination!.droppableId;
        const itemKey = event.draggableId;
        const boardLane = arrangedLanes[destinationLaneKey];
        const boardItem = arrangedItems[event.source.droppableId].find((boardItem) => boardItem.itemKey === itemKey);

        const announcement = interpolate(
            t[
                destinationLaneKey === event.source.droppableId
                    ? "on drag update board item same lane"
                    : "on drag update board item different lane"
            ],
            [
                {
                    itemTitle: getText(t.locale, (boardItem as IPreparedBoardItem).title),
                    itemPosition: event.destination.index + 1,
                    laneLength: arrangedItems[destinationLaneKey].length,
                    laneTitle: getText(t.locale, (boardLane as TBoardLane).title),
                },
            ]
        );

        provided.announce(announcement);

        setPlaceholderPosition(
            getPlaceholderPosition(
                $draggable,
                getClientYChildren($droppable, event.draggableId, event.destination.index)
            )
        );
    };

    const onDragEnd = async ({ draggableId, reason, source, destination }: DropResult, provided: ResponderProvided) => {
        let announcement;

        const boardItem = arrangedItems[source.droppableId].find((boardItem) => boardItem.itemKey === draggableId);

        if (destination) {
            const laneKey = destination.droppableId;
            const boardLane = laneKey && arrangedLanes[laneKey];

            announcement = interpolate(t["on drag end board item"], [
                {
                    itemTitle: getText(t.locale, (boardItem as IPreparedBoardItem).title),
                    itemPosition: Math.max(1, destination.index + 1),
                    laneLength: arrangedItems[laneKey].length,
                    laneTitle: getText(t.locale, (boardLane as TBoardLane).title),
                },
            ]);
        } else {
            announcement = interpolate(t["on drag cancel board item"], [
                {
                    itemTitle: getText(t.locale, (boardItem as IPreparedBoardItem).title),
                },
            ]);
        }

        provided.announce(announcement);

        if (source && destination) {
            const sourceLaneKey = source.droppableId;
            const destinationLaneKey = destination.droppableId;
            if (sourceLaneKey && destinationLaneKey) {
                const movingItems = arrangedItems[sourceLaneKey].splice(source.index, 1);
                arrangedItems[sourceLaneKey].map(resetOrder);
                arrangedItems[destinationLaneKey].splice(destination.index, 0, movingItems[0]);
                arrangedItems[destinationLaneKey].map(resetOrder);
                setPlaceholderPosition(null);

                if (await updateItemPosition(boardItem!, source, destination!)) {
                    setArrangedItems(cloneDeep(arrangedItems));
                }
            }
        }
    };

    const moveLane = (laneKey: string, delta: 1 | -1) => {
        const laneKeys = Object.keys(arrangedLanes);
        const from = laneKeys.indexOf(laneKey);
        laneKeys.splice(from + delta, 0, laneKeys.splice(from, 1)[0]);
        setArrangedLanes(
            laneKeys.reduce((nextArrangedLanes: TBoardLanes, currentLaneKey: string) => {
                nextArrangedLanes[currentLaneKey] = arrangedLanes[currentLaneKey];
                return nextArrangedLanes;
            }, {})
        );
    };

    const deleteLane = (laneKey: string) => {
        setArrangedLanes(omit(arrangedLanes, [laneKey]));
    };

    return (
        <DragDropContext {...{ onDragStart, onDragUpdate, onDragEnd }}>
            <Box
                styles={{
                    overflowX: "auto",
                    flexGrow: 1,
                }}
            >
                <Box styles={{ height: "100%", display: "flex" }} accessibility={boardBehavior}>
                    {Object.keys(arrangedLanes).map((laneKey, laneIndex, laneKeys) => {
                        const last = laneIndex === laneKeys.length - 1;
                        return (
                            <BoardLane
                                first={laneIndex === 0}
                                last={addingLane ? false : last}
                                laneKey={laneKey}
                                lane={arrangedLanes[laneKey]}
                                chosenTaskId={props.chosenTaskId}
                                addItemDialog={
                                    <BoardItemDialog
                                        action={BoardItemDialogAction.Create}
                                        trigger={
                                            <Button
                                                icon={<AddIcon outline />}
                                                iconOnly
                                                fluid
                                                title={t["add board item"]}
                                                aria-label={t["add board item"]}
                                            />
                                        }
                                        initialState={{ lane: laneKey }}
                                        {...{
                                            arrangedLanes,
                                            users,
                                            t,
                                            setArrangedItems,
                                            arrangedItems,
                                        }}
                                    />
                                }
                                editItemDialog={(boardItem: IBoardItem) => (
                                    <>
                                        <BoardItemDialog
                                            action={BoardItemDialogAction.Edit}
                                            trigger={
                                                <MenuItem
                                                    vertical
                                                    icon={<EditIcon outline size="small" />}
                                                    content={t["edit board item"]}
                                                />
                                            }
                                            initialState={boardItem}
                                            {...{
                                                arrangedLanes,
                                                users,
                                                t,
                                                setArrangedItems,
                                                arrangedItems,
                                            }}
                                        />
                                        <Dialog
                                            trigger={
                                                <MenuItem
                                                    vertical
                                                    icon={<TrashCanIcon outline size="small" />}
                                                    content={t["delete"]}
                                                />
                                            }
                                            content={getText(t.locale, t["confirm delete"], {
                                                title: getText(t.locale, boardItem.title),
                                            })}
                                            confirmButton={{ content: t["delete"] }}
                                            cancelButton={{ content: t["cancel"] }}
                                            onConfirm={() => {
                                                const pos = arrangedItems[boardItem.lane].findIndex(
                                                    (laneItem) =>
                                                        laneItem.itemKey === (boardItem as IPreparedBoardItem).itemKey
                                                );
                                                arrangedItems[boardItem.lane].splice(pos, 1);
                                                setArrangedItems(cloneDeep(arrangedItems));
                                            }}
                                        />
                                    </>
                                )}
                                key={`BoardLane__${laneKey}`}
                                preparedItems={arrangedItems[laneKey]}
                                users={users}
                                t={t}
                                rtl={rtl}
                                boardItemCardLayout={props.boardItemCardLayout || defaultBoardItemCardLayout}
                                placeholderPosition={placeholderPosition}
                                moveLane={moveLane}
                                deleteLane={deleteLane}
                                onItemClick={props.onClick}
                            />
                        );
                    })}
                    {addingLane && (
                        <BoardLane
                            last
                            pending
                            laneKey={uniqueId("pl")}
                            key="BoardLane__pending_lane"
                            preparedItems={[]}
                            users={users}
                            t={t}
                            rtl={rtl}
                            boardItemCardLayout={props.boardItemCardLayout || defaultBoardItemCardLayout}
                            placeholderPosition={null}
                            exitPendingLane={(value) => {
                                if (value.length > 0) {
                                    const newLaneKey = uniqueId("sl");
                                    setArrangedLanes(
                                        Object.assign(arrangedLanes, {
                                            [newLaneKey]: { title: value },
                                        })
                                    );
                                    setArrangedItems(Object.assign(arrangedItems, { [newLaneKey]: [] }));
                                }
                                setAddingLane(false);
                            }}
                            onItemClick={props.onClick}
                            chosenTaskId={props.chosenTaskId}
                        />
                    )}
                </Box>
            </Box>
        </DragDropContext>
    );
};

/**
 * @public
 */
export const Board = (props: IBoardProps) => {
    const [arrangedLanes, setStateArrangedLanes] = useState<TBoardLanes>(props.lanes);

    const [arrangedItems, setStateArrangedItems] = useState<IPreparedBoardItems>(
        prepareBoardItems(props.items, props.lanes)
    );

    useEffect(() => {
        setStateArrangedItems(prepareBoardItems(props.items, props.lanes));
    }, [props.items]);

    useEffect(() => {
        setStateArrangedLanes(props.lanes);
    }, [props.lanes]);

    const [addingLane, setAddingLane] = useState<boolean>(false);

    const setArrangedLanes = (lanes: TBoardLanes) => {
        if (props.onInteraction) props.onInteraction({ event: "update", target: "lanes", lanes });
        return setStateArrangedLanes(lanes as SetStateAction<TBoardLanes>);
    };

    const setArrangedItems = (items: IPreparedBoardItems) => {
        if (props.onInteraction) props.onInteraction({ event: "update", target: "items", items });
        return setStateArrangedItems(items as SetStateAction<IPreparedBoardItems>);
    };

    return (
        <FluentUIThemeConsumer
            render={(globalTheme) => {
                const { t, rtl } = globalTheme.siteVariables;
                return (
                    <BoardTheme
                        globalTheme={globalTheme}
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            flexGrow: 1,
                        }}
                    >
                        <Flex
                            column
                            variables={({ colorScheme }: SiteVariablesPrepared) => ({
                                backgroundColor: colorScheme.default.background2,
                            })}
                            styles={{
                                height: "100%",
                            }}
                        >
                            {/* <Toolbar
                                actionGroups={{
                                g1: {
                                    a1: {
                                    icon: "Add",
                                    title: t["add lane"],
                                    subject: "add_column",
                                    },
                                },
                                }}
                                onInteraction={({ subject }: any) => {
                                if (subject === "add_column") setAddingLane(true);
                                }}
                            /> */}
                            {Object.keys(arrangedLanes).length > 0 || addingLane ? (
                                <BoardStandalone
                                    {...{
                                        t,
                                        rtl,
                                        arrangedLanes,
                                        arrangedItems,
                                        setArrangedItems: setArrangedItems as Dispatch<
                                            SetStateAction<IPreparedBoardItems>
                                        >,
                                        addingLane,
                                        setAddingLane,
                                        setArrangedLanes: setArrangedLanes as Dispatch<SetStateAction<TBoardLanes>>,
                                        onClick: props.onShowTaskDetails,
                                        onUpdateItemPosition: props.onChangeTaskStatus,
                                    }}
                                    {...pick(props, ["users", "boardItemCardLayout"])}
                                    chosenTaskId={props.chosenTaskId}
                                />
                            ) : // <Communication
                            //     {...(props.emptyState || {
                            //         option: CommunicationOptions.Empty,
                            //         fields: {
                            //             title: getText(t.locale, t["board empty header"]),
                            //             desc: getText(t.locale, t["board empty body"]),
                            //         },
                            //     })}
                            // />
                            null}
                        </Flex>
                    </BoardTheme>
                );
            }}
        />
    );
};
